// Filter + URL-sync wiring for the fiat-transactions list. Owns the
// drawer filters, the URL-synced quick filters (deep-linked from
// treasury-accounts / wallets / bank-accounts directories), and the
// composed `listParams` the query reads. Lifted out of the container
// so the container stays focused on the list shape itself.

import { computed, ref } from 'vue'

import { useFilterUrlSync } from '@shared/composables/useFilterUrlSync'

import type { FiatTransactionsDrawerFilterValues } from '../components/FiatTransactionsFilterDrawer.vue'
import {
  FIAT_TRANSACTION_REASON,
  FIAT_TRANSACTION_REFERENCE_TYPE,
  FIAT_TRANSACTION_STATUS,
  FIAT_TRANSACTION_TYPE,
  type FiatTransactionReason,
  type FiatTransactionReferenceType,
  type FiatTransactionStatus,
  type FiatTransactionType,
  type FiatTransactionsSortOrder,
  type GetFiatTransactionsParams,
} from '../types/fiatTransactionsTypes'

const DEFAULT_PAGE_SIZE = 50

// URL-synced quick filters: deep-linked from sibling treasury directories'
// "View transactions" actions.
//   - investorBankAccountId  ← bank-accounts directory row action
//   - investorId             ← wallets directory row action
//   - treasuryAccountId      ← treasury-accounts kebab action (also
//                              bound to the inline quick filter UI)
// All three are hidden from the UI — the operator clears them via Reset
// filters or by returning via the sidebar. `treasuryAccountId` stays a
// quick filter the operator can change inline.
interface QuickFilters {
  treasuryAccountId: string | null
  investorBankAccountId: string | null
  investorId: string | null
}

function DEFAULT_DRAWER_FILTERS(): FiatTransactionsDrawerFilterValues {
  return {
    fromDate: null,
    toDate: null,
    investorName: '',
    investorEmail: '',
    status: null,
    referenceType: null,
    transactionType: null,
    transactionReason: null,
    amountMin: '',
    amountMax: '',
    sortOrder: 'desc',
  }
}

function DEFAULT_QUICK_FILTERS(): QuickFilters {
  return {
    treasuryAccountId: null,
    investorBankAccountId: null,
    investorId: null,
  }
}

export function useFiatTransactionsListFilters() {
  const currentPage = ref(1)
  const pageSize = ref(DEFAULT_PAGE_SIZE)

  const quickFilters = ref<QuickFilters>(DEFAULT_QUICK_FILTERS())
  const drawerFilters = ref<FiatTransactionsDrawerFilterValues>(DEFAULT_DRAWER_FILTERS())

  useFilterUrlSync(quickFilters, {
    toQuery: (state) => ({
      treasuryAccountId: state.treasuryAccountId ?? undefined,
      investorBankAccountId: state.investorBankAccountId ?? undefined,
      investorId: state.investorId ?? undefined,
    }),
    fromQuery: (query) => {
      const seed: Partial<QuickFilters> = {}
      if (typeof query.treasuryAccountId === 'string')
        seed.treasuryAccountId = query.treasuryAccountId
      if (typeof query.investorBankAccountId === 'string')
        seed.investorBankAccountId = query.investorBankAccountId
      if (typeof query.investorId === 'string') seed.investorId = query.investorId
      return seed
    },
    onFiltersChange: () => {
      currentPage.value = 1
    },
  })

  useFilterUrlSync(drawerFilters, {
    toQuery: (state) => ({
      fromDate: state.fromDate ? state.fromDate.toISOString() : undefined,
      toDate: state.toDate ? state.toDate.toISOString() : undefined,
      investorName: state.investorName || undefined,
      investorEmail: state.investorEmail || undefined,
      status: state.status ?? undefined,
      referenceType: state.referenceType ?? undefined,
      transactionType: state.transactionType ?? undefined,
      transactionReason: state.transactionReason ?? undefined,
      amountMin: state.amountMin || undefined,
      amountMax: state.amountMax || undefined,
      // Sort order defaults to 'desc' — only persist when the operator
      // flipped it, so a refresh of the default view stays clean.
      sortOrder: state.sortOrder === 'desc' ? undefined : state.sortOrder,
    }),
    fromQuery: (query) => {
      const seed: Partial<FiatTransactionsDrawerFilterValues> = {}
      if (typeof query.fromDate === 'string') {
        const d = new Date(query.fromDate)
        if (!Number.isNaN(d.getTime())) seed.fromDate = d
      }
      if (typeof query.toDate === 'string') {
        const d = new Date(query.toDate)
        if (!Number.isNaN(d.getTime())) seed.toDate = d
      }
      if (typeof query.investorName === 'string') seed.investorName = query.investorName
      if (typeof query.investorEmail === 'string') seed.investorEmail = query.investorEmail
      if (
        typeof query.status === 'string' &&
        Object.values(FIAT_TRANSACTION_STATUS).includes(query.status as FIAT_TRANSACTION_STATUS)
      ) {
        seed.status = query.status as FiatTransactionStatus
      }
      if (
        typeof query.referenceType === 'string' &&
        Object.values(FIAT_TRANSACTION_REFERENCE_TYPE).includes(
          query.referenceType as FIAT_TRANSACTION_REFERENCE_TYPE,
        )
      ) {
        seed.referenceType = query.referenceType as FiatTransactionReferenceType
      }
      if (
        typeof query.transactionType === 'string' &&
        Object.values(FIAT_TRANSACTION_TYPE).includes(
          query.transactionType as FIAT_TRANSACTION_TYPE,
        )
      ) {
        seed.transactionType = query.transactionType as FiatTransactionType
      }
      if (
        typeof query.transactionReason === 'string' &&
        Object.values(FIAT_TRANSACTION_REASON).includes(
          query.transactionReason as FIAT_TRANSACTION_REASON,
        )
      ) {
        seed.transactionReason = query.transactionReason as FiatTransactionReason
      }
      if (typeof query.amountMin === 'string') seed.amountMin = query.amountMin
      if (typeof query.amountMax === 'string') seed.amountMax = query.amountMax
      if (query.sortOrder === 'asc' || query.sortOrder === 'desc') {
        seed.sortOrder = query.sortOrder as FiatTransactionsSortOrder
      }
      return seed
    },
    onFiltersChange: () => {
      currentPage.value = 1
    },
  })

  function dateToIso(date: Date | null): string | undefined {
    if (!date) return undefined
    return new Date(date).toISOString()
  }

  const listParams = computed<GetFiatTransactionsParams>(() => {
    const drawer = drawerFilters.value
    const quick = quickFilters.value
    const params: GetFiatTransactionsParams = {
      page: currentPage.value,
      limit: pageSize.value,
      sortOrder: drawer.sortOrder,
    }
    if (quick.treasuryAccountId) params.treasuryAccountId = quick.treasuryAccountId
    if (quick.investorBankAccountId) params.investorBankAccountId = quick.investorBankAccountId
    if (quick.investorId) params.investorId = quick.investorId
    const fromIso = dateToIso(drawer.fromDate)
    if (fromIso) params.fromDate = fromIso
    const toIsoDate = dateToIso(drawer.toDate)
    if (toIsoDate) params.toDate = toIsoDate
    if (drawer.investorName) params.investorName = drawer.investorName
    if (drawer.investorEmail) params.investorEmail = drawer.investorEmail
    if (drawer.status) params.status = drawer.status
    if (drawer.referenceType) params.referenceType = drawer.referenceType
    if (drawer.transactionType) params.transactionType = drawer.transactionType
    if (drawer.transactionReason) params.transactionReason = drawer.transactionReason
    if (drawer.amountMin) params.amountMin = drawer.amountMin
    if (drawer.amountMax) params.amountMax = drawer.amountMax
    return params
  })

  // Filter count badge — counts only the drawer fields. Quick filters
  // live inline above the table, so they aren't counted in the drawer
  // badge. Sort order is excluded from the badge as well — its default
  // 'desc' isn't a "filter" the operator opened the drawer to set.
  const activeFilterCount = computed(() => {
    const drawer = drawerFilters.value
    let count = 0
    if (drawer.fromDate) count += 1
    if (drawer.toDate) count += 1
    if (drawer.investorName) count += 1
    if (drawer.investorEmail) count += 1
    if (drawer.status) count += 1
    if (drawer.referenceType) count += 1
    if (drawer.transactionType) count += 1
    if (drawer.transactionReason) count += 1
    if (drawer.amountMin) count += 1
    if (drawer.amountMax) count += 1
    return count
  })

  const hasActiveFilters = computed(
    () =>
      activeFilterCount.value > 0 ||
      !!quickFilters.value.treasuryAccountId ||
      !!quickFilters.value.investorBankAccountId ||
      !!quickFilters.value.investorId,
  )

  function applyDrawerFilters(next: FiatTransactionsDrawerFilterValues) {
    drawerFilters.value = { ...next }
    currentPage.value = 1
  }

  function setTreasuryAccountId(value: string | null) {
    quickFilters.value = { ...quickFilters.value, treasuryAccountId: value }
  }

  function resetFilters() {
    drawerFilters.value = DEFAULT_DRAWER_FILTERS()
    quickFilters.value = DEFAULT_QUICK_FILTERS()
    currentPage.value = 1
  }

  return {
    currentPage,
    pageSize,
    drawerFilters,
    quickFilters,
    listParams,
    activeFilterCount,
    hasActiveFilters,
    applyDrawerFilters,
    setTreasuryAccountId,
    resetFilters,
  }
}
